import type { PresentationBlock, PresentationOptionV2, PresentationSectionV2 } from 'paintscout'
import {
  addPresentationNextBlock,
  addPresentationNextSection,
  createPresentationNextSection,
  findBlock,
  findPage,
  findSection
} from '@paintscout/util/builder'

import { PRESENTATION_CANVAS_ID } from './presentationEditorDom'
import { BLOCK_KEY_ATTRIBUTE, VIEWPORT_ANCHOR_INSET } from './presentationViewportAnchor'
import type { BlockRect } from './presentationViewportAnchor'

/**
 * Click-to-insert for the Add panel's block and section tiles (#24863). Drag inserts where it is
 * dropped (canvasCatalogDrop.ts); a click has no drop point, so it inserts after whatever the user
 * is looking at — the block nearest the middle of the visible canvas — and then scrolls the canvas
 * to the new node.
 *
 * The geometry and the tree edit are pure and tested; the two DOM reads and the scroll are thin
 * wrappers at the bottom of the file.
 */

/**
 * Marks a rendered section wrapper in `Presentation.tsx` with its own `key`, the section-level
 * counterpart of `BLOCK_KEY_ATTRIBUTE`. Needed for the empty-section fallback: an empty section has
 * no block to anchor on, but it still has a wrapper.
 */
export const SECTION_KEY_ATTRIBUTE = 'data-section-key'

/**
 * The node whose vertical centre is nearest the centre of the visible canvas (the band between
 * `inset` and `viewportHeight`). Nodes wholly off screen are ignored; `''` when none is on screen.
 */
export function pickCenterKey(args: { rects: BlockRect[]; viewportHeight: number; inset?: number }): string {
  const { rects, viewportHeight, inset = VIEWPORT_ANCHOR_INSET } = args
  const centre = (inset + viewportHeight) / 2

  let best = ''
  let bestDistance = Number.POSITIVE_INFINITY

  for (const rect of rects ?? []) {
    if (rect.bottom <= inset || rect.top >= viewportHeight) {
      continue
    }

    const distance = Math.abs((rect.top + rect.bottom) / 2 - centre)
    // strict `<`: on a tie the earlier node (document order) wins
    if (distance < bestDistance) {
      best = rect.key
      bestDistance = distance
    }
  }

  return best
}

export type CatalogClickItem =
  | { kind: 'block'; block: PresentationBlock }
  | { kind: 'section'; section: PresentationSectionV2 }

export interface CatalogClickInsertResult {
  presentation: PresentationOptionV2
  /** The node to scroll to: the new block, or the new section. `''` when nothing was inserted. */
  scrollKey: string
}

/**
 * Inserts a clicked catalog item into the active page, after the anchor.
 *
 * - Block, anchored on a block: after that block, in its section.
 * - Block, anchored on an (empty) section only: at the end of that section.
 * - Block, no anchor: at the end of the page's last section, or — on a page with no sections — in a
 *   new blank section, since a block cannot live outside one.
 * - Section: after the anchor block's section, or after the anchor section, or at the end of the page.
 *
 * An anchor that no longer resolves (the tree changed since the DOM was read) is treated as no
 * anchor, so a click always lands somewhere on the active page.
 */
export function insertCatalogItem(args: {
  presentation: PresentationOptionV2
  pageKey: string
  anchorBlockKey?: string
  anchorSectionKey?: string
  item: CatalogClickItem
}): CatalogClickInsertResult {
  const { presentation, pageKey, anchorBlockKey, anchorSectionKey, item } = args
  const blockLocation = anchorBlockKey ? findBlock(presentation, anchorBlockKey) : null
  const sectionLocation = blockLocation ?? (anchorSectionKey ? findSection(presentation, anchorSectionKey) : null)
  const pageLocation = findPage(presentation, sectionLocation?.page.key ?? pageKey)

  if (!pageLocation) {
    return { presentation, scrollKey: '' }
  }

  if (item.kind === 'section') {
    return {
      presentation: addPresentationNextSection({
        presentation,
        pageKey: pageLocation.page.key,
        section: item.section,
        index: sectionLocation ? sectionLocation.sectionIndex + 1 : undefined
      }),
      scrollKey: item.section.key
    }
  }

  const { block } = item

  if (blockLocation) {
    return {
      presentation: addPresentationNextBlock({
        presentation,
        sectionKey: blockLocation.section.key,
        block,
        index: blockLocation.blockIndex + 1
      }),
      scrollKey: block.key
    }
  }

  const targetSection = sectionLocation?.section ?? pageLocation.page.sections?.at(-1)

  if (targetSection) {
    return {
      presentation: addPresentationNextBlock({ presentation, sectionKey: targetSection.key, block }),
      scrollKey: block.key
    }
  }

  const newSection = { ...createPresentationNextSection(), blocks: [block] }

  return {
    presentation: addPresentationNextSection({ presentation, pageKey: pageLocation.page.key, section: newSection }),
    scrollKey: block.key
  }
}

/**
 * The window scroll position that centres a node of `height` whose viewport-relative top is `top`.
 * A node taller than the visible band is aligned to the top of the band instead, so its start stays
 * in view.
 */
export function getCentredScrollTop(args: {
  top: number
  height: number
  scrollY: number
  viewportHeight: number
  inset?: number
}): number {
  const { top, height, scrollY, viewportHeight, inset = VIEWPORT_ANCHOR_INSET } = args
  const band = viewportHeight - inset
  const offset = height >= band ? inset : inset + (band - height) / 2

  return Math.max(0, scrollY + top - offset)
}

function readRects(attribute: string, doc: Document): BlockRect[] {
  const canvas = doc.getElementById(PRESENTATION_CANVAS_ID)
  if (!canvas) {
    return []
  }

  return Array.from(canvas.querySelectorAll(`[${attribute}]`)).map((element) => {
    const { top, bottom } = element.getBoundingClientRect()
    return { key: element.getAttribute(attribute) ?? '', top, bottom }
  })
}

/** Reads the canvas for the click anchor: the centre block, and the centre section as a fallback. */
export function readCatalogClickAnchor(doc: Document = document): { anchorBlockKey: string; anchorSectionKey: string } {
  const viewportHeight = (doc.defaultView ?? window).innerHeight

  return {
    anchorBlockKey: pickCenterKey({ rects: readRects(BLOCK_KEY_ATTRIBUTE, doc), viewportHeight }),
    anchorSectionKey: pickCenterKey({ rects: readRects(SECTION_KEY_ATTRIBUTE, doc), viewportHeight })
  }
}

/**
 * Smooth-scrolls the window (the canvas has no scroll container of its own) to centre the node with
 * id `key`. The node was just added to Formik values and has not rendered yet, so this waits two
 * frames (the same wait `PresentationEdit`'s selection scroll uses), then keeps checking once per
 * frame, up to `maxFrames`, for a slow render. It gives up silently after that.
 */
export const SCROLL_TO_NODE_MAX_FRAMES = 30

export function scrollToCanvasNode(key: string, win: Window = window, maxFrames = SCROLL_TO_NODE_MAX_FRAMES) {
  if (!key) {
    return
  }

  const attempt = (frame: number) => {
    const element = frame >= 2 ? win.document.getElementById(key) : null

    if (!element) {
      if (frame < maxFrames) {
        win.requestAnimationFrame(() => attempt(frame + 1))
      }
      return
    }

    const { top, height } = element.getBoundingClientRect()
    win.scrollTo({
      top: getCentredScrollTop({ top, height, scrollY: win.scrollY, viewportHeight: win.innerHeight }),
      behavior: 'smooth'
    })
  }

  win.requestAnimationFrame(() => attempt(1))
}
